<template>
  <div class="company-edit-page">
    <v-container fluid class="px-6 py-4">
      <!-- Page Header -->
      <div class="page-header mb-6">
        <div class="header-content">
          <div class="page-title-section">
            <div class="d-flex align-center mb-2">
              <v-btn
                v-if="roleName == 'SUPER ADMIN' && company.id == null"
                icon
                color="primary"
                @click="$router.back()"
                class="back-btn mr-3"
              >
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <h1 class="page-title">
                <v-icon large class="vibrant-title-icon mr-3">mdi-office-building-cog</v-icon>
                Edit Company
                <span class="page-inline-subtitle">Update company details and settings</span>
              </h1>
              <v-spacer></v-spacer>
              <div class="header-actions">
                <!-- Header actions removed - button moved to bottom -->
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Company Form Card -->
      <v-card class="company-form-card" elevation="8">
        <v-card-title class="modern-card-title">
          <h2 class="card-title">
            <v-icon class="mr-2">mdi-form-select</v-icon>
            Company Information
          </h2>
        </v-card-title>
        <v-card-text class="pa-4">
          <!-- Basic Information Section -->
          <div class="form-section mb-6">
            <h3 class="section-title mb-4">
              <v-icon class="mr-2">mdi-information-outline</v-icon>
              Basic Information
            </h3>
            <v-row>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  v-model="form.name"
                  outlined
                  label="Company Name *"
                  prepend-inner-icon="mdi-office-building"
                  :error-messages="errors.name"
                  class="minimal-input"
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  v-model="form.tag_line"
                  outlined
                  label="Tag Line"
                  prepend-inner-icon="mdi-tag-text"
                  :error-messages="errors.tag_line"
                  class="minimal-input"
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  v-model="form.email"
                  outlined
                  label="Email *"
                  prepend-inner-icon="mdi-email"
                  :error-messages="errors.email"
                  class="minimal-input"
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  v-model="form.phone"
                  outlined
                  label="Phone *"
                  prepend-inner-icon="mdi-phone"
                  :error-messages="errors.phone"
                  class="minimal-input"
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  v-model="form.admin_name"
                  outlined
                  label="Admin Name"
                  prepend-inner-icon="mdi-account-supervisor"
                  class="minimal-input"
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-col>
            </v-row>
          </div>

          <v-divider class="my-5"></v-divider>

          <!-- Address Information Section -->
          <div class="form-section mb-6">
            <h3 class="section-title mb-4">
              <v-icon class="mr-2">mdi-map-marker</v-icon>
              Address Information
            </h3>
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.address"
                  outlined
                  label="Address *"
                  prepend-inner-icon="mdi-map-marker-multiple"
                  :error-messages="errors.address"
                  class="minimal-input"
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model="form.city"
                  outlined
                  label="City"
                  prepend-inner-icon="mdi-city"
                  :error-messages="errors.city"
                  class="minimal-input"
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model="form.state"
                  outlined
                  label="State"
                  prepend-inner-icon="mdi-map"
                  :error-messages="errors.state"
                  class="minimal-input"
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model="form.pincode"
                  outlined
                  label="Pincode"
                  prepend-inner-icon="mdi-numeric"
                  :error-messages="errors.pincode"
                  class="minimal-input"
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-col>
            </v-row>
          </div>

          <v-divider class="my-5"></v-divider>

          <!-- Business Information Section -->
          <div class="form-section mb-6">
            <h3 class="section-title mb-4">
              <v-icon class="mr-2">mdi-chart-line</v-icon>
              Business Information
            </h3>
            <v-row>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  v-model="form.tax_percent"
                  outlined
                  label="Tax Percent"
                  prepend-inner-icon="mdi-percent"
                  :error-messages="errors.tax_percent"
                  class="minimal-input"
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  v-model="form.packaging_cost"
                  outlined
                  label="Packaging Costs"
                  prepend-inner-icon="mdi-currency-usd"
                  :error-messages="errors.packaging_cost"
                  class="minimal-input"
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <v-switch
                  v-model="form.is_active"
                  inset
                  color="primary"
                  :label="`Status: ${form.is_active ? 'Active' : 'Inactive'}`"
                  class="minimal-switch mt-2"
                  hide-details
                ></v-switch>
              </v-col>
            </v-row>
          </div>

          <v-divider class="my-5"></v-divider>

          <!-- Logo Upload Section -->
          <div class="form-section mb-6">
            <h3 class="section-title mb-4">
              <v-icon class="mr-2">mdi-image</v-icon>
              Company Logo
            </h3>
            <v-row>
              <v-col cols="12" md="6">
                <div class="logo-upload-section">
                  <v-file-input
                    ref="file"
                    label="Upload Company Logo"
                    prepend-icon="mdi-camera"
                    accept="image/*"
                    outlined
                    dense
                    class="minimal-input"
                    @change="onFileChange"
                    hide-details="auto"
                  ></v-file-input>
                  
                  <div v-if="image_url" class="logo-preview mt-4">
                    <div class="preview-header mb-2">
                      <span class="preview-label">Logo Preview</span>
                      <v-btn
                        x-small
                        color="error"
                        outlined
                        @click="removeImage"
                        class="ml-2"
                      >
                        <v-icon small left>mdi-close</v-icon>
                        Remove
                      </v-btn>
                    </div>
                    <div class="preview-container">
                      <img :src="image_url" alt="Company Logo" class="logo-preview-image" />
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
          </div>

          <v-divider class="my-5"></v-divider>

          <!-- Content Management Section -->
          <div class="form-section">
            <h3 class="section-title mb-4">
              <v-icon class="mr-2">mdi-file-document-edit</v-icon>
              Content Management
            </h3>
            <v-row>
              <v-col cols="12">
                <label for="about-us" class="content-label">About Us</label>
                <vue-editor
                  ref="about us"
                  v-model="form.about_us"
                  class="form-control minimal-editor"
                />
              </v-col>
              <v-col cols="12">
                <label for="terms-conditions" class="content-label">Terms & Conditions</label>
                <vue-editor
                  ref="term condition"
                  v-model="form.terms"
                  class="form-control minimal-editor"
                />
              </v-col>
              <v-col cols="12">
                <label for="privacy-policy" class="content-label">FAQs</label>
                <vue-editor ref="faqs" v-model="form.faqs" class="form-control minimal-editor" />
              </v-col>
              <v-col cols="12">
                <label for="privacy" class="content-label">Privacy Policy</label>
                <vue-editor
                  ref="privacy"
                  v-model="form.privacy"
                  class="form-control minimal-editor"
                />
              </v-col>
              <v-col cols="12">
                <label for="return-policy" class="content-label">Return Policy</label>
                <vue-editor
                  ref="return policy"
                  v-model="form.return_policy"
                  class="form-control minimal-editor"
                />
              </v-col>
              <v-col cols="12">
                <label for="refund-policy" class="content-label">Refund Policy</label>
                <vue-editor
                  ref="refund policy"
                  v-model="form.refund_policy"
                  class="form-control minimal-editor"
                />
              </v-col>
              <v-col cols="12">
                <label for="cancellation-policy" class="content-label">Cancellation Policy</label>
                <vue-editor
                  ref="cancellation policy"
                  v-model="form.cancellation_policy"
                  class="form-control minimal-editor"
                />
              </v-col>
              <v-col cols="12">
                <label for="disclaimer" class="content-label">Disclaimer</label>
                <vue-editor
                  ref="disclaimer"
                  v-model="form.disclaimer"
                  class="form-control minimal-editor"
                />
              </v-col>
            </v-row>
          </div>
        </v-card-text>
        
        <!-- Form Actions -->
        <v-card-actions class="minimal-actions pa-4">
          <v-spacer></v-spacer>
          <v-btn 
            text
            @click="$router.back()"
            class="minimal-cancel-btn mr-2"
          >
            <v-icon left small>mdi-arrow-left</v-icon>
            Cancel
          </v-btn>
          <v-btn 
            color="primary" 
            :loading="isLoading"
            :disabled="isLoading"
            @click="save"
            class="minimal-save-btn"
            elevation="1"
          >
            <v-icon left small>mdi-content-save</v-icon>
            Update Company
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-container>
  </div>
</template>
<script>
import { VueEditor } from "vue2-editor";
import axios from "@/axios";
export default {
  name: "CompanyEditPage",
  components: {
    VueEditor,
  },
  data() {
    return {
      form: {
        name: "",
        is_active: true,
      },
      errors: {},
      image_url: "",
      old_json: {},
      isLoading: false,
    };
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      this.isLoading = true;
      let encryptedId = this.$route.params.companyId;
      axios
        .get(`/companies/${encryptedId}`)
        .then((response) => {
          this.form = response.data.data;
          this.old_json = response.data.data;
          this.image_url = this.form.logo_path
            ? `${this.mediaUrl + this.form.logo_path}`
            : "";
        })
        .catch((error) => {
          // redirect to Home Page
          window.location.href = "/auth/login";
          console.error("Error fetching data:", error);
        });
      this.isLoading = false;
    },
    async save() {
      try {
        this.isLoading = true;
        await axios.patch(
          `/companies/${this.$route.params.companyId}`,
          this.form
        );
        await this.handleFileUpload();
        await this.saveUserTimestamp(this.old_json, this.form);
        this.isLoading = false;
        if (
          this.roleName == "ADMIN" ||
          (this.roleName == "SUPER ADMIN" && this.company.id != null)
        ) {
          window.location.href = "/auth/login";
        } else {
          this.$router.push("/companies");
        }
      } catch (e) {
        console.log(e);
        this.isLoading = false;
      }
    },
    async handleFileUpload() {
      console.log("called");
      if (this.$refs.file != null) {
        console.log("uploading");
        let attachment = this.$refs.file.files[0];
        let formData = new FormData();
        formData.append("id", this.form.id);
        formData.append("logo_path", attachment);
        await axios
          .post("upload_company_logo_path", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .catch(function (error) {
            console.log(error);
            console.log("FAILURE!!");
          });
      }
    },
    onFileChange() {
      this.warningImageLoader = false;
      this.file = this.$refs.file?.files[0];
      let reader = new FileReader();
      reader.readAsDataURL(this.file);
      reader.onload = (evt) => {
        let img = new Image();
        img.onload = () => {
          this.imageLoader = false;
          this.image_url = URL.createObjectURL(this.file);
          this.imageErrors = "";
        };
        img.src = evt.target.result;
      };

      reader.onerror = (evt) => {
        console.error(evt);
      };
      // this.image_url = URL.createObjectURL(this.file);
    },
    removeImage() {
      this.image_url = "";
      this.file = null;
      if (this.$refs.file) {
        this.$refs.file.value = null;
      }
    },
  },
};
</script>

<style scoped>
.company-edit-page {
  min-height: calc(100vh - 64px);
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

/* Page Header */
.page-header {
  background: white;
  border-radius: 20px;
  padding: 16px 20px;
  box-shadow: 0 10px 40px rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.1);
  position: relative;
  overflow: hidden;
}

.page-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

.page-inline-subtitle {
  font-size: 16px;
  color: #64748b;
  font-weight: 400;
  margin-left: 16px;
  display: inline;
}

.page-title-section {
  flex: 1;
  width: 100%;
}

.vibrant-title-icon {
  background: linear-gradient(135deg, #ff6b6b 0%, #4ecdc4 25%, #667eea 50%, #764ba2 75%, #f093fb 100%) !important;
  -webkit-background-clip: text !important;
  background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
  font-weight: 900 !important;
  filter: drop-shadow(0 2px 6px rgba(102, 126, 234, 0.4)) drop-shadow(0 0 12px rgba(255, 107, 107, 0.3));
  animation: pulse-glow 3s ease-in-out infinite;
  font-size: 32px !important;
}

@keyframes pulse-glow {
  0%, 100% { 
    filter: drop-shadow(0 2px 6px rgba(102, 126, 234, 0.4)) drop-shadow(0 0 12px rgba(255, 107, 107, 0.3));
    transform: scale(1);
  }
  50% { 
    filter: drop-shadow(0 4px 12px rgba(102, 126, 234, 0.6)) drop-shadow(0 0 20px rgba(255, 107, 107, 0.5));
    transform: scale(1.05);
  }
}

.back-btn {
  border-radius: 12px !important;
  background: rgba(102, 126, 234, 0.1) !important;
  border: 1px solid rgba(102, 126, 234, 0.2);
  transition: all 0.3s ease;
}

.back-btn:hover {
  background: rgba(102, 126, 234, 0.15) !important;
  transform: translateY(-1px);
}

/* Company Form Card */
.company-form-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  background: white;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(102, 126, 234, 0.15) !important;
}

.modern-card-title {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid rgba(102, 126, 234, 0.1);
  padding: 24px !important;
  margin: 0 !important;
}

.card-title {
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

/* Form Sections */
.form-section {
  padding: 16px 0;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  display: flex;
  align-items: center;
  margin: 0;
}

.section-title .v-icon {
  color: #667eea;
  margin-right: 8px;
}

/* Minimal Input Styling */
.minimal-input {
  margin-bottom: 8px;
}

.minimal-input >>> .v-input__control {
  min-height: 56px;
}

.minimal-input >>> .v-input__control .v-input__slot {
  background: #f8f9fa !important;
  border: 1.5px solid #e2e8f0 !important;
  border-radius: 12px !important;
  transition: all 0.3s ease;
  padding: 0 16px !important;
  margin: 0 !important;
  box-shadow: none !important;
}

.minimal-input >>> .v-input__control .v-input__slot:before,
.minimal-input >>> .v-input__control .v-input__slot:after {
  display: none !important;
}

.minimal-input >>> .v-input__control .v-input__slot:hover {
  border-color: #cbd5e1 !important;
  background: #ffffff !important;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1) !important;
}

.minimal-input >>> .v-input--is-focused .v-input__control .v-input__slot {
  border-color: #667eea !important;
  background: #ffffff !important;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
}

.minimal-input >>> .v-label {
  color: #64748b !important;
  font-weight: 500;
  font-size: 14px;
}

.minimal-input >>> .v-label--active {
  color: #667eea !important;
  transform: translateY(-18px) scale(0.85) !important;
}

.minimal-input >>> input {
  color: #1e293b !important;
  font-weight: 500;
  font-size: 15px;
  padding: 8px 0 !important;
}

.minimal-input >>> input::placeholder {
  color: #94a3b8 !important;
  font-weight: 400;
}

.minimal-input >>> .v-input__prepend-inner .v-icon {
  color: #667eea !important;
  margin-right: 12px;
}

/* Remove fieldset borders */
.minimal-input >>> fieldset {
  display: none !important;
}

/* Switch Styling */
.minimal-switch >>> .v-input--switch__thumb {
  color: #667eea !important;
}

.minimal-switch >>> .v-input--switch__track {
  color: #e2e8f0 !important;
}

.minimal-switch >>> .v-input--switch.v-input--is-label-active .v-input--switch__track {
  color: #667eea !important;
}

.minimal-switch >>> .v-label {
  color: #2c3e50 !important;
  font-weight: 500;
  font-size: 15px;
}

/* Logo Upload Section */
.logo-upload-section {
  padding: 16px;
  border: 2px dashed #e2e8f0;
  border-radius: 12px;
  background: #f8f9fa;
  transition: all 0.3s ease;
}

.logo-upload-section:hover {
  border-color: #667eea;
  background: rgba(102, 126, 234, 0.05);
}

.logo-preview {
  display: flex;
  flex-direction: column;
}

.preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.preview-label {
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
}

.preview-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.logo-preview-image {
  max-width: 120px;
  max-height: 80px;
  object-fit: contain;
  border-radius: 4px;
}

/* Content Management */
.content-label {
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
  display: block;
}

.minimal-editor {
  border-radius: 12px;
  overflow: hidden;
  border: 1.5px solid #e2e8f0;
  transition: all 0.3s ease;
}

.minimal-editor:hover {
  border-color: #cbd5e1;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1);
}

.minimal-editor:focus-within {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

/* Form Actions */
.minimal-actions {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid rgba(102, 126, 234, 0.1);
  padding: 20px 24px !important;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}

.minimal-cancel-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 500 !important;
  padding: 0 20px !important;
  height: 44px !important;
  color: #64748b !important;
  border: 1.5px solid #e2e8f0 !important;
  background: white !important;
  transition: all 0.3s ease;
}

.minimal-cancel-btn:hover {
  border-color: #cbd5e1 !important;
  background: #f8fafc !important;
  transform: translateY(-1px);
}

.minimal-save-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  padding: 0 24px !important;
  height: 44px !important;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  border: none !important;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3) !important;
}

.minimal-save-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4) !important;
}

.minimal-save-btn:before {
  background-color: transparent;
}

/* Dividers */
.v-divider {
  border-color: rgba(102, 126, 234, 0.1) !important;
  margin: 20px 0 !important;
}

/* Error Messages */
.v-messages {
  color: #ef4444 !important;
  font-size: 12px !important;
  font-weight: 500 !important;
}

/* Responsive Design */
@media (max-width: 960px) {
  .page-title {
    font-size: 22px;
  }
  
  .page-inline-subtitle {
    display: block;
    margin-left: 0;
    margin-top: 4px;
    font-size: 14px;
  }
  
  .page-header {
    padding: 14px 18px;
  }
  
  .modern-card-title {
    padding: 20px !important;
  }
  
  .card-title {
    font-size: 18px;
  }
}

@media (max-width: 600px) {
  .page-header {
    padding: 12px 16px;
  }
  
  .page-title {
    font-size: 20px;
  }
  
  .modern-card-title {
    padding: 16px !important;
  }
  
  .minimal-actions {
    flex-direction: column-reverse;
    gap: 8px;
  }
  
  .minimal-cancel-btn,
  .minimal-save-btn {
    width: 100%;
  }
  
  .logo-upload-section {
    padding: 12px;
  }
  
  .logo-preview-image {
    max-width: 100px;
    max-height: 60px;
  }
}
</style>